import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import { SA, SaPageHeader } from "../../components/superadmin/saUi";
import PackageCard from "../../components/subscription/PackageCard";
import { toast } from "react-toastify";
import { COLORS } from "../../assets/theme";
import {
  createPlan,
  listPlans,
  PLATFORM_KEYS,
  PLATFORM_LABELS,
  PlanInput,
  PlatformKey,
  SubscriptionPlan,
  updatePlan,
} from "../../services/superadminService";
import { getErrorMessage } from "../../utils/apiResponse";

type PlatformChoice = PlatformKey | "ALL";

interface PlanForm {
  name: string;
  description: string;
  price: string;
  currency: string;
  durationDays: string;
  platform: PlatformChoice;
  features: string;
  sortOrder: string;
  isActive: boolean;
}

const EMPTY_FORM: PlanForm = {
  name: "",
  description: "",
  price: "",
  currency: "USD",
  durationDays: "30",
  platform: "CLINIC",
  features: "",
  sortOrder: "0",
  isActive: true,
};

const toForm = (plan: SubscriptionPlan): PlanForm => ({
  name: plan.name,
  description: plan.description ?? "",
  price: String(plan.price),
  currency: plan.currency,
  durationDays: String(plan.durationDays),
  platform: plan.platform ?? "ALL",
  features: plan.features.join("\n"),
  sortOrder: String(plan.sortOrder),
  isActive: plan.isActive,
});

const Plans: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<SubscriptionPlan | "new" | null>(null);
  const [form, setForm] = useState<PlanForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPlans(await listPlans());
    } catch {
      // The interceptor already surfaced the message.
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditing("new");
  };

  const openEdit = (plan: SubscriptionPlan) => {
    setForm(toForm(plan));
    setEditing(plan);
  };

  const set = <K extends keyof PlanForm>(key: K, value: PlanForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const price = Number(form.price);
  const duration = Number(form.durationDays);
  const formValid =
    form.name.trim() !== "" &&
    form.price.trim() !== "" &&
    price >= 0 &&
    Number.isInteger(duration) &&
    duration >= 1 &&
    /^[A-Za-z]{3}$/.test(form.currency.trim());

  const save = async () => {
    if (!formValid || !editing) return;
    const payload: PlanInput = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      price,
      currency: form.currency.trim().toUpperCase(),
      durationDays: duration,
      platform: form.platform === "ALL" ? null : form.platform,
      features: form.features
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean),
      sortOrder: Number(form.sortOrder) || 0,
      isActive: form.isActive,
    };
    setSaving(true);
    try {
      if (editing === "new") await createPlan(payload);
      else await updatePlan(editing.id, payload);
      toast.success(`Package ${editing === "new" ? "created" : "saved"}.`, {
        position: "top-right",
      });
      setEditing(null);
      load();
    } catch (err: any) {
      toast.error(getErrorMessage(err, "Could not save the package."), {
        position: "top-right",
      });
    }
    setSaving(false);
  };

  const toggleArchived = async (plan: SubscriptionPlan) => {
    try {
      await updatePlan(plan.id, { isActive: !plan.isActive });
      load();
    } catch (err: any) {
      toast.error(getErrorMessage(err, "Could not update the package."), {
        position: "top-right",
      });
    }
  };

  const groups: { key: PlatformChoice; title: string }[] = [
    ...PLATFORM_KEYS.map((key) => ({ key, title: PLATFORM_LABELS[key] })),
    { key: "ALL", title: "Every platform" },
  ];

  return (
    <Box>
      <SaPageHeader
        title="Packages"
        subtitle='What owners see when their trial or subscription ends. Until online payment is connected they tap "Contact us" and you activate them from the organization page.'
        icon={<WorkspacePremiumOutlinedIcon />}
        tone="violet"
        actions={
          <Button
            variant="contained"
            disableElevation
            startIcon={<AddIcon />}
            onClick={openNew}
            sx={{ textTransform: "none", minHeight: 40, borderRadius: "10px", fontWeight: 600 }}
          >
            New package
          </Button>
        }
      />

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={26} />
        </Box>
      )}

      {!loading && plans.length === 0 && (
        <Paper
          elevation={0}
          sx={{ border: `1px dashed ${SA.border}`, borderRadius: SA.radius, bgcolor: SA.surface, p: 4, textAlign: "center" }}
        >
          <Typography sx={{ fontWeight: 600 }}>No packages yet</Typography>
          <Typography sx={{ color: COLORS.placeHolderColor, fontSize: "0.9rem", mb: 2 }}>
            Create the first one, for example "Clinic Monthly, 29 USD / 30 days".
          </Typography>
          <Button variant="outlined" onClick={openNew} sx={{ textTransform: "none" }}>
            New package
          </Button>
        </Paper>
      )}

      {!loading &&
        groups.map((group) => {
          const groupPlans = plans.filter(
            (plan) => (plan.platform ?? "ALL") === group.key
          );
          if (groupPlans.length === 0) return null;
          return (
            <Box key={group.key} sx={{ mb: 3 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", mb: 1.25 }}>
                {group.title}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    lg: "repeat(3, 1fr)",
                  },
                }}
              >
                {groupPlans.map((plan) => (
                  <PackageCard
                    key={plan.id}
                    pkg={plan}
                    footer={
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openEdit(plan)}
                          sx={{ textTransform: "none", minHeight: 36 }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          onClick={() => toggleArchived(plan)}
                          sx={{ textTransform: "none", minHeight: 36 }}
                        >
                          {plan.isActive ? "Archive" : "Restore"}
                        </Button>
                      </Box>
                    }
                  />
                ))}
              </Box>
            </Box>
          );
        })}

      <Dialog
        open={!!editing}
        onClose={() => !saving && setEditing(null)}
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editing === "new" ? "New package" : "Edit package"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              inputProps={{ maxLength: 80 }}
              required
            />
            <TextField
              label="Short description"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              inputProps={{ maxLength: 500 }}
            />
            <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr 1fr", sm: "2fr 1fr 1.5fr" } }}>
              {/* Text, not type="number": a number input reports an empty value
                  for "9." mid-typing, which wiped the decimal point. */}
              <TextField
                label="Price"
                value={form.price}
                onChange={(e) => {
                  const raw = e.target.value.replace(",", ".");
                  if (/^\d*\.?\d*$/.test(raw)) set("price", raw);
                }}
                inputProps={{ inputMode: "decimal" }}
                required
              />
              <TextField
                label="Currency"
                value={form.currency}
                onChange={(e) => set("currency", e.target.value.toUpperCase())}
                inputProps={{ maxLength: 3 }}
                helperText="e.g. USD"
              />
              <TextField
                label="Length (days)"
                type="number"
                value={form.durationDays}
                onChange={(e) => set("durationDays", e.target.value)}
                inputProps={{ min: 1, max: 3650 }}
                helperText="30 = month, 365 = year"
                sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }}
                required
              />
            </Box>
            <TextField
              select
              label="Offered on"
              value={form.platform}
              onChange={(e) => set("platform", e.target.value as PlatformChoice)}
            >
              {PLATFORM_KEYS.map((key) => (
                <MenuItem key={key} value={key}>
                  {PLATFORM_LABELS[key]} only
                </MenuItem>
              ))}
              <MenuItem value="ALL">Every platform</MenuItem>
            </TextField>
            <TextField
              label="What's included"
              value={form.features}
              onChange={(e) => set("features", e.target.value)}
              multiline
              minRows={3}
              helperText="One line per bullet point."
            />
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
              <TextField
                label="Order"
                type="number"
                value={form.sortOrder}
                onChange={(e) => set("sortOrder", e.target.value)}
                helperText="Lower shows first"
                sx={{ width: 140 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={(e) => set("isActive", e.target.checked)}
                  />
                }
                label="Shown to owners"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditing(null)} disabled={saving} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={!formValid || saving}
            sx={{ textTransform: "none" }}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Plans;
